import { InboxView } from '@weave/schema-gen-ts/dist/schemas/sms/data/v4/sms_service.pb';
import { Bool } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { ThreadAIOutcome } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { formatPhoneNumberE164 } from '@frontend/phone-numbers';
import { isSmsMessageItem } from '../../../utils/thread-items-utils';
import { ListUnifiedThreadsIO } from '../../types';
import { convertUnifiedThreadToApplicableThreadReadStatuses } from './convert-unified-thread-to-applicable-thread-read-statuses';

type ThreadCouldBeInListUnifiedThreadsResponseArgs = {
  thread: Thread;
  listUnifiedThreadsRequest: ListUnifiedThreadsIO['input'];
};

export const threadCouldBeInListUnifiedThreadsResponse = ({
  thread,
  listUnifiedThreadsRequest,
}: ThreadCouldBeInListUnifiedThreadsResponseArgs): boolean => {
  // locationIds
  if (
    listUnifiedThreadsRequest.locationIds.length &&
    !listUnifiedThreadsRequest.locationIds.includes(thread.locationId)
  ) {
    return false;
  }

  // filter.departmentIds - For unified threads, departmentId is not at thread level, check first SMS item
  if (listUnifiedThreadsRequest.filter?.departmentIds?.length) {
    const firstSmsItem = thread.items?.find(isSmsMessageItem);
    const threadDepartmentId = firstSmsItem?.smsMessage?.departmentId;

    if (!threadDepartmentId || !listUnifiedThreadsRequest.filter.departmentIds.includes(threadDepartmentId)) {
      return false;
    }
  }

  // filter.personPhones - Check first SMS item in items array
  if (listUnifiedThreadsRequest.filter?.personPhones?.length && thread.items?.length) {
    const firstSmsItem = thread.items.find(isSmsMessageItem);

    if (!firstSmsItem?.smsMessage?.personPhone) {
      return false;
    }

    const matchesPersonPhone = listUnifiedThreadsRequest.filter.personPhones.some(
      (personPhone) => formatPhoneNumberE164(personPhone) === formatPhoneNumberE164(firstSmsItem.smsMessage.personPhone)
    );

    if (!matchesPersonPhone) {
      return false;
    }
  }

  // filter.locationPhones - Check thread.locationPhone or first SMS item's locationPhone
  if (listUnifiedThreadsRequest.filter?.locationPhones?.length) {
    const firstSmsItem = thread.items?.find(isSmsMessageItem);
    const threadLocationPhone = thread.locationPhone || firstSmsItem?.smsMessage?.locationPhone || '';

    const matchesLocationPhone = listUnifiedThreadsRequest.filter.locationPhones.some(
      (locationPhone) => formatPhoneNumberE164(locationPhone) === formatPhoneNumberE164(threadLocationPhone)
    );

    if (!matchesLocationPhone) {
      return false;
    }
  }

  // filter.statuses
  const statusFilters = listUnifiedThreadsRequest.filter?.statuses ?? [];
  const hasStatusFilters = !!statusFilters.length;
  const applicableThreadReadStatuses = convertUnifiedThreadToApplicableThreadReadStatuses(thread);
  if (
    hasStatusFilters &&
    applicableThreadReadStatuses?.length &&
    !applicableThreadReadStatuses.some((threadReadStatus) =>
      listUnifiedThreadsRequest.filter?.statuses?.includes(threadReadStatus)
    )
  ) {
    return false;
  }

  // filter.inboxView
  const inboxView = listUnifiedThreadsRequest.filter?.inboxView ?? InboxView.UNSPECIFIED;
  if (inboxView === InboxView.ACTIONABLE && thread.actionable === false) {
    return false;
  } else if (inboxView === InboxView.BLOCKED && thread.isBlocked === false) {
    return false;
  } else if (inboxView !== InboxView.BLOCKED && thread.isBlocked === true) {
    return false;
  } else if (inboxView === InboxView.ARCHIVED && !thread.isArchived) {
    return false;
  } else if (inboxView !== InboxView.ARCHIVED && thread.isArchived) {
    return false;
  }

  // filter.excludeAutomatedLastMessage is not supported for thread updates. SMS updaters should be used instead.

  // filter.personIds
  if (
    listUnifiedThreadsRequest.filter?.personIds?.length &&
    !listUnifiedThreadsRequest.filter.personIds.includes(thread.person?.personId ?? 'invalid-id') // Always false if personId isn't present
  ) {
    return false;
  }

  // filter.messageTags - For unified threads, uniqueTags are InboxTags with entityId instead of SMSTags with smsId
  if (
    listUnifiedThreadsRequest.filter?.messageTags?.length &&
    !listUnifiedThreadsRequest.filter.messageTags.some((tagId) => thread.uniqueTags?.some((tag) => tag.tagId === tagId))
  ) {
    return false;
  }

  // filter.taskStatuses
  if (listUnifiedThreadsRequest.filter?.taskStatuses?.length) {
    const threadTasks = thread.tasks || [];
    if (threadTasks.length === 0) {
      return false; // Thread has no tasks, shouldn't be visible when task status filter is applied
    }

    // Check if any of the thread's tasks match the filtered task statuses
    const hasMatchingTaskStatus = threadTasks.some((task) =>
      listUnifiedThreadsRequest.filter?.taskStatuses?.includes(task.status)
    );

    if (!hasMatchingTaskStatus) {
      return false;
    }
  }

  // filter unifiedFilter (ai activity)
  if (listUnifiedThreadsRequest.unifiedFilter?.isActive === Bool.BOOL_TRUE) {
    const isActive = !!thread.aiStatus?.isActive;
    if (!isActive) {
      return false;
    }
  }

  if (listUnifiedThreadsRequest.unifiedFilter?.isActive === Bool.BOOL_FALSE) {
    const isActive = !!thread.aiStatus?.isActive;
    if (isActive) {
      return false;
    }
  }

  if (listUnifiedThreadsRequest.unifiedFilter?.needsAttention === Bool.BOOL_TRUE) {
    const isEscalated = !!thread.aiStatus?.isEscalated;
    if (!isEscalated) {
      return false;
    }
  }

  if (listUnifiedThreadsRequest.unifiedFilter?.aiOutcomes?.length) {
    const aiOutcomes = listUnifiedThreadsRequest.unifiedFilter?.aiOutcomes;

    if (thread.aiStatus?.status === undefined || !aiOutcomes.includes(thread.aiStatus.status)) {
      return false;
    }

    const outcomes = [
      ThreadAIOutcome.THREAD_AI_OUTCOME_SCHEDULING_COMPLETE,
      ThreadAIOutcome.THREAD_AI_OUTCOME_BOOKING_COMPLETION,
      ThreadAIOutcome.THREAD_AI_OUTCOME_RESCHEDULING_COMPLETION,
      ThreadAIOutcome.THREAD_AI_OUTCOME_ASSISTANCE_COMPLETE,
      ThreadAIOutcome.THREAD_AI_OUTCOME_APPOINTMENT_CONFIRMATION,
      ThreadAIOutcome.THREAD_AI_OUTCOME_CANCELLATION_COMPLETION,
    ];

    if (!outcomes.includes(thread.aiStatus.status)) {
      return false;
    }
  }

  return true;
};
